/**
 * Mirrors app/Services/PricingService.php exactly.
 *
 * Every page that quotes a price (the pricing page, the admin calculator)
 * imports these — one implementation, so a preview can never disagree with
 * what checkout actually charges.
 */

export interface Tier {
    from_usd: number;
    credits_per_usd: number;
}

/** The credits-per-$1 rate that applies to a given spend. */
export function rateForCents(cents: number, tiers: Tier[], baseRate: number): number {
    let rate = baseRate;

    tiers.forEach((t) => {
        if (cents / 100 + 1e-9 >= t.from_usd) rate = t.credits_per_usd;
    });

    return rate;
}

/** Credits a user receives for paying this many cents. */
export function creditsForPurchase(cents: number, tiers: Tier[], baseRate: number): number {
    return Math.floor((cents / 100) * rateForCents(cents, tiers, baseRate));
}

/** The next better tier above this spend, for "spend $x more" nudges. */
export function nextTierForCents(cents: number, tiers: Tier[], baseRate: number): Tier | null {
    const rate = rateForCents(cents, tiers, baseRate);

    return tiers.find((t) => t.from_usd > cents / 100 && t.credits_per_usd > rate) ?? null;
}

/** The payment processor's cut of a charge, in cents. */
export function processingFeeCents(cents: number, percent: number, fixedCents: number): number {
    return cents > 0 ? (cents * percent) / 100 + fixedCents : 0;
}

export const usd = (cents: number, dp = 2) =>
    '$' + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });

export const pct = (fraction: number, dp = 1) =>
    (fraction * 100).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }) + '%';
